import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Calendar, Activity, AlertTriangle, Clock, User } from 'lucide-react';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, patientsRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/patients`)
      ]);
      
      setStats(statsRes.data);
      
      // Create patients lookup
      const patientsMap = {};
      patientsRes.data.forEach(p => {
        patientsMap[p.id] = p;
      });
      setPatients(patientsMap);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Patients Actifs',
      value: stats?.patients_actifs || 0,
      icon: Users,
      color: 'bg-blue-500',
      textColor: 'text-blue-600'
    },
    {
      title: "Rendez-vous Aujourd'hui",
      value: stats?.rendez_vous_aujourdhui || 0,
      icon: Calendar,
      color: 'bg-green-500',
      textColor: 'text-green-600'
    },
    {
      title: 'Perfusions en Cours',
      value: stats?.perfusions_en_cours || 0,
      icon: Activity,
      color: 'bg-orange-500',
      textColor: 'text-orange-600'
    },
    {
      title: 'Alertes',
      value: stats?.alertes || 0,
      icon: AlertTriangle,
      color: 'bg-red-500',
      textColor: 'text-red-600'
    }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Tableau de Bord</h1>
        <p className="text-gray-600 mt-1">Bienvenue, Dr. {user.prenom} {user.nom}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="card-hover animate-slide-in" style={{ animationDelay: `${index * 0.1}s` }} data-testid={`stat-card-${index}`}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">
                  {stat.title}
                </CardTitle>
                <div className={`w-10 h-10 rounded-full ${stat.color} bg-opacity-10 flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${stat.textColor}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-gray-900">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="today-appointments-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Aujourd'hui</CardTitle>
            <Calendar className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            {stats?.appointments_today && stats.appointments_today.length > 0 ? (
              <div className="space-y-3">
                {stats.appointments_today.map((appointment) => {
                  const patient = patients[appointment.patient_id];
                  const startTime = new Date(appointment.start).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  
                  return (
                    <div
                      key={appointment.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate('/appointments')}
                      data-testid={`dashboard-appointment-${appointment.id}`}
                    >
                      <div className="flex items-center space-x-3">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="font-medium text-gray-900">{startTime}</p>
                          {patient && (
                            <p className="text-sm text-gray-600">
                              {patient.identite.prenom} {patient.identite.nom}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          appointment.statut === 'termine' ? 'bg-green-100 text-green-700' :
                          appointment.statut === 'en_cours' ? 'bg-yellow-100 text-yellow-700' :
                          appointment.statut === 'annule' ? 'bg-red-100 text-red-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {appointment.type}
                        </span>
                        {appointment.statut === 'annule' && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            Annulé
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>Aucun rendez-vous aujourd'hui</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="upcoming-appointments-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>À Venir (2 mois)</CardTitle>
            <Activity className="w-5 h-5 text-green-600" />
          </CardHeader>
          <CardContent>
            {stats?.appointments_upcoming && stats.appointments_upcoming.length > 0 ? (
              <div className="space-y-3">
                {stats.appointments_upcoming.slice(0, 5).map((appointment) => {
                  const patient = patients[appointment.patient_id];
                  const appointmentDate = new Date(appointment.start);
                  const dateStr = appointmentDate.toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'short'
                  });
                  const timeStr = appointmentDate.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                  
                  return (
                    <div
                      key={appointment.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate('/appointments')}
                      data-testid={`dashboard-upcoming-${appointment.id}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="text-center">
                          <p className="text-xs text-gray-500 uppercase">{dateStr}</p>
                          <p className="text-sm font-medium text-gray-900">{timeStr}</p>
                        </div>
                        {patient && (
                          <div className="flex items-center space-x-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <p className="text-sm text-gray-600">
                              {patient.identite.prenom} {patient.identite.nom}
                            </p>
                          </div>
                        )}
                      </div>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        {appointment.type}
                      </span>
                    </div>
                  );
                })}
                {stats.appointments_upcoming.length > 5 && (
                  <button
                    onClick={() => navigate('/appointments')}
                    className="w-full text-center py-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Voir tous ({stats.appointments_upcoming.length})
                  </button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>Aucun rendez-vous prévu</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
